import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { io } from "socket.io-client";
import {
  Radio, Power, PowerOff, Send, Cpu, RefreshCw, Zap, FolderOpen,
  Wifi, WifiOff, Play, Square, Activity, AlertTriangle, CheckCircle,
  Clock, ArrowRight, Eye, X, Sparkles, Pause
} from "lucide-react";
import Header from "../components/layout/Header";
import { noeudsAPI, projetsAPI } from "../api/services";
import { getTypeConfig } from "../utils/simulatorTypes";
import { inverseCalibration, rawUnitForMode } from "../utils/calibration";
import { useSocket } from "../context/SocketContext";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:5000";

/* ────────────────────────────── Page ────────────────────────────── */

export default function Simulateur() {
  const { t } = useTranslation();
  const [projets, setProjets] = useState([]);
  const [selectedProjet, setSelectedProjet] = useState("");
  const [noeuds, setNoeuds] = useState([]);
  const [loading, setLoading] = useState(false);

  // Sockets simulés (un par device "incarné")
  const socketsRef = useRef({});
  const [deviceStates, setDeviceStates] = useState({}); // { sn: { simulating, dbEtat, dbValeur, inputValue, autoMode, intervalId } }

  // Selection
  const [selectedSn, setSelectedSn] = useState(null);
  const [related, setRelated] = useState(null); // { evenements, fonctions, automatisations }
  const [loadingRelated, setLoadingRelated] = useState(false);

  // Mode prédictif
  const [predictValue, setPredictValue] = useState("");
  const [prediction, setPrediction] = useState(null);
  const [predicting, setPredicting] = useState(false);

  // Timeline
  const [timeline, setTimeline] = useState([]); // [{ ts, kind, msg, ref }]
  const [highlightSn, setHighlightSn] = useState(null);

  // Live updates depuis le dashboard socket (data:new, alert:new, device:statusChange)
  const { lastSensorData, lastStatusChange, lastAlert } = useSocket() || {};

  /* ── Charger projets ── */
  useEffect(() => {
    projetsAPI.getAll({ limit: 200 }).then(({ data }) => {
      const list = data.data || data;
      setProjets(Array.isArray(list) ? list : []);
    }).catch(err => console.error("projets:", err));
    return () => {
      Object.values(socketsRef.current).forEach(s => s?.disconnect());
    };
  }, []);

  /* ── Charger nœuds quand projet change ── */
  useEffect(() => {
    if (!selectedProjet) { setNoeuds([]); setDeviceStates({}); setSelectedSn(null); return; }
    setLoading(true);
    setSelectedSn(null);
    setRelated(null);
    Object.keys(socketsRef.current).forEach(sn => {
      socketsRef.current[sn]?.disconnect();
      delete socketsRef.current[sn];
    });
    noeudsAPI.getAll({ projet: selectedProjet, limit: 500 }).then(({ data }) => {
      const list = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
      setNoeuds(list);
      const states = {};
      list.forEach(n => {
        states[n.sn] = {
          simulating: false,
          dbEtat: n.etat || "Hors ligne",
          dbValeur: n.derniereValeur,
          inputValue: n.derniereValeur != null
            ? parseFloat(n.derniereValeur)
            : Math.round((getTypeConfig(n.type).min + getTypeConfig(n.type).max) / 2),
          autoMode: false,
          intervalId: null,
        };
      });
      setDeviceStates(states);
    }).finally(() => setLoading(false));
  }, [selectedProjet]);

  /* ── Charger les règles liées au nœud sélectionné ── */
  useEffect(() => {
    if (!selectedSn) { setRelated(null); setPrediction(null); return; }
    const noeud = noeuds.find(n => n.sn === selectedSn);
    if (!noeud) return;
    setLoadingRelated(true);
    noeudsAPI.relatedRules(noeud._id)
      .then(({ data }) => setRelated(data.data))
      .catch(() => setRelated(null))
      .finally(() => setLoadingRelated(false));
  }, [selectedSn, noeuds]);

  /* ── Mise à jour live des valeurs DB depuis le dashboard socket ── */
  useEffect(() => {
    if (!lastSensorData) return;
    setDeviceStates(prev => {
      if (!prev[lastSensorData.sn]) return prev;
      return { ...prev, [lastSensorData.sn]: { ...prev[lastSensorData.sn], dbValeur: lastSensorData.value, dbEtat: "En ligne" } };
    });
  }, [lastSensorData]);

  useEffect(() => {
    if (!lastStatusChange) return;
    setDeviceStates(prev => {
      if (!prev[lastStatusChange.sn]) return prev;
      return { ...prev, [lastStatusChange.sn]: { ...prev[lastStatusChange.sn], dbEtat: lastStatusChange.etat } };
    });
    pushTimeline({
      kind: lastStatusChange.etat === "En ligne" ? "online" : "offline",
      msg: `${lastStatusChange.sn} ${lastStatusChange.etat === "En ligne" ? "connecté" : "déconnecté"}`,
      ref: lastStatusChange.sn,
    });
  }, [lastStatusChange]);

  useEffect(() => {
    if (!lastAlert) return;
    pushTimeline({
      kind: lastAlert.type === "Critique" ? "alert-critical" : "alert",
      msg: `${lastAlert.label}${lastAlert.desc ? ` — ${lastAlert.desc.slice(0, 80)}` : ""}`,
      ref: lastAlert.sn,
    });
  }, [lastAlert]);

  const pushTimeline = useCallback((entry) => {
    setTimeline(prev => [{ ts: new Date().toLocaleTimeString(), ...entry }, ...prev.slice(0, 99)]);
  }, []);

  /* ── Simulation : ouvrir un socket /devices et incarner le device ── */
  const startSim = useCallback((noeud) => {
    const { sn } = noeud;
    if (socketsRef.current[sn]?.connected) return;
    const socket = io(`${SOCKET_URL}/devices`, {
      transports: ["websocket", "polling"],
      reconnection: true,
      auth: { key: process.env.REACT_APP_IOT_DEVICE_KEY || "Sagrada-iot26" }
    });

    socket.on("connect", () => {
      socket.emit("device:register", { sn, mode: "push" });
      setDeviceStates(prev => ({ ...prev, [sn]: { ...prev[sn], simulating: true, dbEtat: "En ligne" } }));
      pushTimeline({ kind: "sim", msg: `Simulation démarrée pour ${sn}`, ref: sn });
    });

    socket.on("device:registered", (data) => {
      // Stocke la calibration reçue pour pouvoir inverse-convertir lors des emit
      setDeviceStates(prev => ({ ...prev, [sn]: { ...prev[sn], calibration: data?.calibration || null } }));
    });

    socket.on("command:execute", (cmd) => {
      const newVal = cmd.action === "Activer" ? 1 : 0;
      setDeviceStates(prev => ({ ...prev, [sn]: { ...prev[sn], dbValeur: newVal, inputValue: newVal } }));
      pushTimeline({
        kind: "command",
        msg: `${sn} reçoit "${cmd.action}" (source: ${cmd.params?.source || "—"})`,
        ref: sn,
      });
      setHighlightSn(sn);
      setTimeout(() => setHighlightSn(null), 1500);
    });

    socket.on("disconnect", () => {
      setDeviceStates(prev => ({ ...prev, [sn]: { ...prev[sn], simulating: false, dbEtat: "Hors ligne" } }));
      pushTimeline({ kind: "sim-stop", msg: `Simulation arrêtée pour ${sn}`, ref: sn });
    });

    socketsRef.current[sn] = socket;
  }, [pushTimeline]);

  const stopSim = useCallback((sn) => {
    const s = socketsRef.current[sn];
    if (s) { s.disconnect(); delete socketsRef.current[sn]; }
    setDeviceStates(prev => {
      const st = prev[sn];
      if (st?.intervalId) clearInterval(st.intervalId);
      return { ...prev, [sn]: { ...st, simulating: false, autoMode: false, intervalId: null } };
    });
  }, []);

  const sendData = useCallback((noeud, value) => {
    const socket = socketsRef.current[noeud.sn];
    if (!socket?.connected) return;
    const cfg = getTypeConfig(noeud.type);
    const v = parseFloat(value);

    // Si le nœud a une calibration (ex: 4-20 mA → 0-100 °C), l'utilisateur saisit du physique
    // mais on doit envoyer du brut au serveur pour qu'il re-convertisse normalement.
    const calibration = deviceStates[noeud.sn]?.calibration;
    let valueToSend = v;
    let unitToSend = cfg.unit;
    if (calibration && calibration.mode && calibration.mode !== "none") {
      const raw = inverseCalibration(v, calibration);
      valueToSend = Math.round(raw * 1000) / 1000;
      unitToSend = rawUnitForMode(calibration.mode) || cfg.unit;
    }

    socket.emit("sensor:data", {
      sn: noeud.sn, type: noeud.type, value: valueToSend, unit: unitToSend,
      timestamp: new Date().toISOString(), metadata: { simulator: true },
    });
    setDeviceStates(prev => ({ ...prev, [noeud.sn]: { ...prev[noeud.sn], dbValeur: v, inputValue: v } }));
    const physMsg = (calibration?.mode && calibration.mode !== "none")
      ? `${noeud.sn} envoie ${v} ${cfg.unit} (brut: ${valueToSend} ${unitToSend})`
      : `${noeud.sn} envoie ${v} ${cfg.unit}`;
    pushTimeline({ kind: "sensor", msg: physMsg, ref: noeud.sn });
    setHighlightSn(noeud.sn);
    setTimeout(() => setHighlightSn(null), 1500);
  }, [pushTimeline, deviceStates]);

  const toggleActuator = useCallback((noeud, activate) => {
    const socket = socketsRef.current[noeud.sn];
    if (!socket?.connected) return;
    socket.emit("sensor:data", {
      sn: noeud.sn, type: noeud.type, value: activate ? 1 : 0, unit: "",
      timestamp: new Date().toISOString(), metadata: { simulator: true, manual: true },
    });
    setDeviceStates(prev => ({ ...prev, [noeud.sn]: { ...prev[noeud.sn], dbValeur: activate ? 1 : 0 } }));
    pushTimeline({ kind: "manual", msg: `${noeud.sn} ${activate ? "activé" : "désactivé"} manuellement (sim)`, ref: noeud.sn });
  }, [pushTimeline]);

  /* ── Mode prédictif (what-if) ── */
  const runPredict = useCallback(async () => {
    const noeud = noeuds.find(n => n.sn === selectedSn);
    if (!noeud || predictValue === "") return;
    setPredicting(true);
    try {
      const { data } = await noeudsAPI.predict(noeud._id, parseFloat(predictValue));
      setPrediction(data.data);
    } catch (e) {
      setPrediction({ error: e.response?.data?.error || "Erreur prédiction" });
    } finally {
      setPredicting(false);
    }
  }, [selectedSn, noeuds, predictValue]);

  /* ── Dérivés ── */
  const capteurs = noeuds.filter(n => n.categorie === "Capteur");
  const actionneurs = noeuds.filter(n => n.categorie === "Actionneur");
  const passerelles = noeuds.filter(n => n.categorie === "Passerelle");
  const selected = noeuds.find(n => n.sn === selectedSn);
  const selectedState = selected ? deviceStates[selected.sn] : null;
  const selectedCfg = selected ? getTypeConfig(selected.type) : null;

  const simulatedCount = Object.values(deviceStates).filter(s => s.simulating).length;
  const onlineCount = Object.values(deviceStates).filter(s => s.dbEtat === "En ligne").length;

  /* ── Rendu ── */
  return (
    <>
      <Header
        title="Simulateur — Maquette logique"
        subtitle="Capteurs → Règles → Actionneurs : visualise la chaîne causale en temps réel"
      />

      {/* Sélection projet + stats globales */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
            <FolderOpen size={18} className="text-violet-600" />
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Projet</label>
            <select
              value={selectedProjet}
              onChange={e => setSelectedProjet(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— Sélectionner un projet —</option>
              {projets.map(p => (
                <option key={p._id} value={p._id}>{p.nom}{p.client?.nom ? ` (${p.client.nom})` : ""}</option>
              ))}
            </select>
          </div>
          {selectedProjet && (
            <div className="flex gap-2">
              <Stat label="Connectés" value={`${onlineCount}/${noeuds.length}`} color="text-green-600" />
              <Stat label="Simulés" value={simulatedCount} color="text-purple-600" />
            </div>
          )}
        </div>
      </div>

      {!selectedProjet ? (
        <EmptyHint />
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw size={24} className="text-slate-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* ══════ 3 COLONNES ══════ */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
            {/* Capteurs */}
            <div className="lg:col-span-4">
              <ColumnHeader icon={Activity} color="text-blue-600" title="Capteurs" count={capteurs.length} desc="Entrées (température, niveau, humidité…)" />
              <div className="space-y-2">
                {capteurs.map(n => (
                  <CapteurCard
                    key={n._id}
                    noeud={n}
                    state={deviceStates[n.sn]}
                    selected={selectedSn === n.sn}
                    highlighted={highlightSn === n.sn}
                    onSelect={() => setSelectedSn(n.sn)}
                    onSimToggle={() => deviceStates[n.sn]?.simulating ? stopSim(n.sn) : startSim(n)}
                    onSend={(v) => sendData(n, v)}
                    onChangeInput={(v) => setDeviceStates(p => ({ ...p, [n.sn]: { ...p[n.sn], inputValue: v } }))}
                  />
                ))}
                {capteurs.length === 0 && <EmptyCol label="Aucun capteur" />}
              </div>
            </div>

            {/* Règles (centre) */}
            <div className="lg:col-span-4">
              <ColumnHeader icon={Sparkles} color="text-amber-600" title="Règles" count={(related?.evenements?.length || 0) + (related?.fonctions?.length || 0) + (related?.automatisations?.length || 0)} desc={selected ? `Liées à ${selected.sn}` : "Sélectionne un nœud pour voir ses règles"} />
              {!selected ? (
                <EmptyCol label="Sélectionne un capteur ou actionneur à gauche/droite" />
              ) : loadingRelated ? (
                <div className="text-center py-6"><RefreshCw size={16} className="animate-spin text-slate-400 mx-auto" /></div>
              ) : (
                <div className="space-y-2">
                  {(related?.evenements || []).map(e => <EventCard key={e._id} evt={e} highlightSn={highlightSn} />)}
                  {(related?.fonctions || []).map(f => <FonctionCard key={f._id} fn={f} />)}
                  {(related?.automatisations || []).map(a => <AutoCard key={a._id} auto={a} />)}
                  {!(related?.evenements?.length || related?.fonctions?.length || related?.automatisations?.length) && (
                    <EmptyCol label="Aucune règle ne mentionne ce nœud" />
                  )}
                </div>
              )}
            </div>

            {/* Actionneurs */}
            <div className="lg:col-span-4">
              <ColumnHeader icon={Zap} color="text-orange-600" title="Actionneurs" count={actionneurs.length} desc="Sorties (pompe, vanne, climatiseur…)" />
              <div className="space-y-2">
                {actionneurs.map(n => (
                  <ActionneurCard
                    key={n._id}
                    noeud={n}
                    state={deviceStates[n.sn]}
                    selected={selectedSn === n.sn}
                    highlighted={highlightSn === n.sn}
                    onSelect={() => setSelectedSn(n.sn)}
                    onSimToggle={() => deviceStates[n.sn]?.simulating ? stopSim(n.sn) : startSim(n)}
                    onToggleOp={(activate) => toggleActuator(n, activate)}
                  />
                ))}
                {actionneurs.length === 0 && <EmptyCol label="Aucun actionneur" />}
              </div>
            </div>
          </div>

          {/* Passerelles */}
          {passerelles.length > 0 && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Passerelles ({passerelles.length})</p>
              <div className="flex flex-wrap gap-2">
                {passerelles.map(n => (
                  <div key={n._id} className="inline-flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5">
                    <Radio size={14} className="text-violet-600" />
                    <span className="text-xs font-mono font-bold text-slate-700">{n.sn}</span>
                    <ConnDot etat={deviceStates[n.sn]?.dbEtat} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mode prédictif (what-if) */}
          {selected?.categorie === "Capteur" && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <Eye size={18} className="text-indigo-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Simulation à blanc — What if ?</p>
                  <p className="text-xs text-slate-500">Prédit ce qui se passerait si {selected.sn} valait X, sans rien envoyer au backend</p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  value={predictValue}
                  onChange={e => setPredictValue(e.target.value)}
                  placeholder={`Valeur (${selectedCfg.min}–${selectedCfg.max} ${selectedCfg.unit})`}
                  step={selectedCfg.step}
                  className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button onClick={runPredict} disabled={predictValue === "" || predicting}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-40">
                  {predicting ? <RefreshCw size={14} className="animate-spin" /> : "Prédire"}
                </button>
                {prediction && (
                  <button onClick={() => setPrediction(null)} className="p-2 text-slate-400 hover:text-slate-600">
                    <X size={16} />
                  </button>
                )}
              </div>
              {prediction && <PredictionResult prediction={prediction} />}
            </div>
          )}

          {/* TIMELINE */}
          <div className="bg-slate-900 rounded-2xl p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-200 flex items-center gap-2">
                <Clock size={16} /> Timeline causale
              </h3>
              {timeline.length > 0 && (
                <button onClick={() => setTimeline([])} className="text-xs text-slate-400 hover:text-slate-200">Effacer</button>
              )}
            </div>
            <div className="font-mono text-xs space-y-1 max-h-72 overflow-y-auto">
              {timeline.length === 0 && (
                <p className="text-slate-500 italic">En attente d'événements… démarre une simulation pour voir la chaîne causale.</p>
              )}
              {timeline.map((e, i) => (
                <TimelineRow key={i} entry={e} onClickRef={(sn) => sn && setSelectedSn(sn)} />
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ────────────────────── Petits composants ────────────────────── */

function Stat({ label, value, color }) {
  return (
    <div className="bg-slate-50 rounded-xl px-3 py-2 text-center min-w-[80px]">
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
    </div>
  );
}

function EmptyHint() {
  return (
    <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 text-center">
      <FolderOpen size={48} className="text-slate-200 mx-auto mb-3" />
      <p className="text-slate-500 font-medium">Sélectionne un projet pour commencer</p>
      <p className="text-xs text-slate-400 mt-1">La maquette affichera ses capteurs, règles et actionneurs.</p>
    </div>
  );
}

function EmptyCol({ label }) {
  return <div className="text-xs text-slate-400 text-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">{label}</div>;
}

function ColumnHeader({ icon: Icon, color, title, count, desc }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className={color} />
        <h3 className="text-sm font-bold text-slate-700">{title}</h3>
        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{count}</span>
      </div>
      <p className="text-[11px] text-slate-400">{desc}</p>
    </div>
  );
}

function ConnDot({ etat }) {
  const online = etat === "En ligne";
  return <span className={`w-2 h-2 rounded-full ${online ? "bg-green-500" : "bg-slate-300"}`} title={etat} />;
}

function StateBadge({ kind, label }) {
  const cls = {
    online: "bg-green-100 text-green-700",
    offline: "bg-slate-200 text-slate-500",
    running: "bg-emerald-100 text-emerald-700",
    stopped: "bg-amber-100 text-amber-700",
    sim: "bg-purple-100 text-purple-700",
  }[kind] || "bg-slate-100 text-slate-600";
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${cls}`}>{label}</span>;
}

function CapteurCard({ noeud, state, selected, highlighted, onSelect, onSimToggle, onSend, onChangeInput }) {
  const cfg = getTypeConfig(noeud.type);
  const Icon = cfg.icon;
  const isOnline = state?.dbEtat === "En ligne";
  const isSim = state?.simulating;
  return (
    <div
      onClick={onSelect}
      className={`bg-white rounded-xl p-3 border cursor-pointer transition-all ${
        selected ? "border-indigo-400 ring-2 ring-indigo-100" :
        highlighted ? "border-blue-400 ring-2 ring-blue-200 animate-pulse" :
        isSim ? "border-purple-200" : "border-gray-100 hover:border-gray-200"
      }`}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.bg}`}>
          <Icon size={14} className={cfg.color} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono font-bold text-xs text-slate-800 truncate">{noeud.sn}</p>
          <p className="text-[10px] text-slate-400 truncate">{noeud.type}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {isSim && <StateBadge kind="sim" label="SIM" />}
          <StateBadge kind={isOnline ? "online" : "offline"} label={isOnline ? "Connecté" : "Hors ligne"} />
        </div>
      </div>

      <div className="bg-slate-50 rounded-lg p-2 mb-2">
        <div className="flex items-baseline justify-between">
          <span className="text-xl font-bold text-slate-800">{isOnline && state?.dbValeur != null ? state.dbValeur : "—"}</span>
          <span className="text-[10px] text-slate-400">{cfg.unit}</span>
        </div>
        {!isOnline && state?.dbValeur != null && (
          <p className="text-[10px] text-slate-400 italic mt-0.5">Dernière valeur connue (hors ligne)</p>
        )}
      </div>

      {isSim && (
        <div onClick={e => e.stopPropagation()} className="space-y-2">
          <div className="flex gap-1">
            <input
              type="number"
              value={state.inputValue ?? ""}
              onChange={e => onChangeInput(parseFloat(e.target.value) || 0)}
              step={cfg.step} min={cfg.min} max={cfg.max}
              className="flex-1 px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono text-center"
            />
            <button onClick={() => onSend(state.inputValue)}
              className="px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              <Send size={11} />
            </button>
          </div>
          <input type="range" min={cfg.min} max={cfg.max} step={cfg.step}
            value={state.inputValue ?? cfg.min}
            onChange={e => onChangeInput(parseFloat(e.target.value))}
            className="w-full accent-indigo-600 cursor-pointer"
          />
        </div>
      )}

      <button
        onClick={e => { e.stopPropagation(); onSimToggle(); }}
        className={`w-full mt-2 flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-lg ${
          isSim ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-purple-50 text-purple-700 hover:bg-purple-100"
        }`}
      >
        {isSim ? <><Square size={11} /> Arrêter sim</> : <><Play size={11} /> Démarrer sim</>}
      </button>
    </div>
  );
}

function ActionneurCard({ noeud, state, selected, highlighted, onSelect, onSimToggle, onToggleOp }) {
  const cfg = getTypeConfig(noeud.type);
  const Icon = cfg.icon;
  const isOnline = state?.dbEtat === "En ligne";
  const isSim = state?.simulating;
  const isRunning = state?.dbValeur === 1;
  return (
    <div
      onClick={onSelect}
      className={`bg-white rounded-xl p-3 border cursor-pointer transition-all ${
        selected ? "border-indigo-400 ring-2 ring-indigo-100" :
        highlighted ? "border-orange-400 ring-2 ring-orange-200 animate-pulse" :
        isRunning && isOnline ? "border-emerald-200" :
        isSim ? "border-purple-200" : "border-gray-100 hover:border-gray-200"
      }`}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.bg}`}>
          <Icon size={14} className={cfg.color} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono font-bold text-xs text-slate-800 truncate">{noeud.sn}</p>
          <p className="text-[10px] text-slate-400 truncate">{noeud.type}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {isSim && <StateBadge kind="sim" label="SIM" />}
          <StateBadge kind={isOnline ? "online" : "offline"} label={isOnline ? "Connecté" : "Hors ligne"} />
          {isOnline && <StateBadge kind={isRunning ? "running" : "stopped"} label={isRunning ? "▶ En marche" : "■ Arrêté"} />}
        </div>
      </div>

      {isSim && isOnline && (
        <div onClick={e => e.stopPropagation()} className="flex gap-1 mt-2">
          {isRunning ? (
            <button onClick={() => onToggleOp(false)}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100">
              <PowerOff size={11} /> Arrêter (manuel)
            </button>
          ) : (
            <button onClick={() => onToggleOp(true)}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
              <Power size={11} /> Démarrer (manuel)
            </button>
          )}
        </div>
      )}

      <button
        onClick={e => { e.stopPropagation(); onSimToggle(); }}
        className={`w-full mt-2 flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-lg ${
          isSim ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-purple-50 text-purple-700 hover:bg-purple-100"
        }`}
      >
        {isSim ? <><Square size={11} /> Arrêter sim</> : <><Play size={11} /> Démarrer sim</>}
      </button>
    </div>
  );
}

function EventCard({ evt, highlightSn }) {
  const isPaused = evt.statut !== "Actif";
  const conds = evt.conditions || [];
  const cooldownLeft = evt.cooldown && evt.dernier
    ? Math.max(0, evt.cooldown * 60 - Math.floor((Date.now() - new Date(evt.dernier).getTime()) / 1000))
    : 0;
  return (
    <div className={`rounded-xl p-3 border ${isPaused ? "bg-slate-50 border-slate-200" : "bg-white border-amber-200"}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkles size={12} className={isPaused ? "text-slate-400" : "text-amber-600"} />
          <p className="text-xs font-bold text-slate-800">{evt.nom}</p>
        </div>
        <div className="flex gap-1">
          {evt.priorite === "Critique" && <StateBadge kind="stopped" label="CRITIQUE" />}
          <StateBadge kind={isPaused ? "offline" : "online"} label={evt.statut} />
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mb-1">
        {evt.role === "capteur" ? "Ce capteur déclenche cet événement" : "Cet événement peut piloter cet actionneur"}
      </p>
      {conds.length > 0 && (
        <div className="space-y-1 mt-2">
          {conds.map((c, i) => (
            <div key={i} className="text-[11px] bg-slate-50 rounded px-2 py-1 font-mono text-slate-700">
              {c.condition} {c.valeur} → <span className={c.action === "Activer" ? "text-emerald-600" : "text-red-600"}>{c.action}</span>
            </div>
          ))}
        </div>
      )}
      {evt.actionsOnThisNode?.length > 0 && (
        <p className="text-[11px] text-slate-600 mt-2">Action sur ce nœud : <strong>{evt.actionsOnThisNode.join(", ")}</strong></p>
      )}
      {cooldownLeft > 0 && (
        <p className="text-[10px] text-orange-600 mt-1 flex items-center gap-1">
          <Clock size={10} /> Cooldown : {Math.floor(cooldownLeft / 60)}:{String(cooldownLeft % 60).padStart(2, "0")} restant
        </p>
      )}
    </div>
  );
}

function FonctionCard({ fn }) {
  const isPaused = fn.statut !== "Actif";
  const cap = fn.capteurConfig;
  return (
    <div className={`rounded-xl p-3 border ${isPaused ? "bg-slate-50 border-slate-200" : "bg-white border-purple-200"}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <Activity size={12} className={isPaused ? "text-slate-400" : "text-purple-600"} />
          <p className="text-xs font-bold text-slate-800">{fn.nom}</p>
        </div>
        <StateBadge kind={isPaused ? "offline" : "online"} label={fn.statut} />
      </div>
      {cap && (
        <p className="text-[11px] text-slate-600 font-mono bg-slate-50 rounded px-2 py-1 mt-2">
          {cap.analyse} sur {cap.periode} {cap.condition} {cap.valeur}
        </p>
      )}
      {fn.actionneurConfig && (
        <p className="text-[11px] text-slate-600 mt-1">
          Action : <strong className={fn.actionneurConfig.action === "Activer" ? "text-emerald-600" : "text-red-600"}>{fn.actionneurConfig.action}</strong>
        </p>
      )}
    </div>
  );
}

function AutoCard({ auto }) {
  const isPaused = auto.statut !== "Actif";
  const jourNoms = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const jours = (auto.selectedJours || []).map(j => jourNoms[j]).join(", ");
  return (
    <div className={`rounded-xl p-3 border ${isPaused ? "bg-amber-50 border-amber-200" : "bg-white border-blue-200"}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <Clock size={12} className={isPaused ? "text-amber-600" : "text-blue-600"} />
          <p className="text-xs font-bold text-slate-800">{auto.nom}</p>
        </div>
        {isPaused ? (
          <StateBadge kind="stopped" label="EN PAUSE" />
        ) : (
          <StateBadge kind="online" label={auto.statut} />
        )}
      </div>
      <p className="text-[10px] text-slate-400 mb-1">{jours || "—"}</p>
      <div className="space-y-1">
        {(auto.creneaux || []).map((c, i) => (
          <div key={i} className="text-[11px] bg-slate-50 rounded px-2 py-1 font-mono flex justify-between">
            <span>{c.debut}–{c.fin}</span>
            <span className={c.action === "Activer" ? "text-emerald-600" : "text-red-600"}>{c.action}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PredictionResult({ prediction }) {
  if (prediction.error) {
    return <p className="text-xs text-red-600 mt-2">{prediction.error}</p>;
  }
  const { triggered = [], inactive = [] } = prediction;
  return (
    <div className="mt-3 space-y-2">
      {triggered.length === 0 && inactive.length === 0 && (
        <p className="text-xs text-slate-500 italic">Aucun événement actif n'utilise ce capteur.</p>
      )}
      {triggered.map((t, i) => (
        <div key={i} className="bg-white rounded-lg p-2 border border-emerald-200">
          <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
            <CheckCircle size={12} /> {t.evenementNom} se déclencherait
          </p>
          <div className="text-[11px] text-slate-600 mt-1 space-y-0.5">
            {t.commands.map((c, j) => (
              <div key={j} className="flex items-center gap-1">
                <ArrowRight size={10} />
                <span className={c.action === "Activer" ? "text-emerald-600" : "text-red-600"}>{c.action}</span>
                <span className="font-mono">{c.sn}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      {inactive.map((i, k) => (
        <div key={k} className="bg-white rounded-lg p-2 border border-slate-200">
          <p className="text-xs text-slate-600 flex items-center gap-1">
            <Pause size={12} /> {i.evenementNom} <span className="text-slate-400">— {i.reason}</span>
          </p>
        </div>
      ))}
    </div>
  );
}

function TimelineRow({ entry, onClickRef }) {
  const colorMap = {
    sensor: "text-blue-300",
    command: "text-amber-300",
    manual: "text-cyan-300",
    online: "text-green-400",
    offline: "text-slate-400",
    sim: "text-purple-300",
    "sim-stop": "text-red-300",
    alert: "text-yellow-300",
    "alert-critical": "text-red-400",
  };
  const iconMap = {
    sensor: <Activity size={11} />,
    command: <Zap size={11} />,
    manual: <Power size={11} />,
    online: <Wifi size={11} />,
    offline: <WifiOff size={11} />,
    sim: <Play size={11} />,
    "sim-stop": <Square size={11} />,
    alert: <AlertTriangle size={11} />,
    "alert-critical": <AlertTriangle size={11} />,
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500 flex-shrink-0">{entry.ts}</span>
      <span className={colorMap[entry.kind] || "text-slate-300"}>{iconMap[entry.kind]}</span>
      <span
        className={`${colorMap[entry.kind] || "text-slate-300"} ${entry.ref ? "cursor-pointer hover:underline" : ""}`}
        onClick={() => entry.ref && onClickRef(entry.ref)}
      >
        {entry.msg}
      </span>
    </div>
  );
}
